import React, { useEffect, useMemo, useState } from 'react';
import { useTable, usePagination } from 'react-table';
import { CARS } from '../../constants/columns';
import { useOutletContext, useParams } from 'react-router-dom';
import { RiErrorWarningLine } from 'react-icons/ri';
import styles from './deal.module.css';

import addItemsToDeal from '../../api/deals/addItemsToDeal';
import getDealCard from '../../api/deals/getDealCard';
import getDealCars from '../../api/deals/getDealCars';
import getCreditAppeal from '../../api/deals/getCreditAppeal';
import ReassignCarButton from '../../components/CreditApplications/ReassignCarButton';
import { DEAL_STATUS_TTL_EXPIRED } from '../../constants/dealStatuses';
import formatNumber from '../../utils/formatNumber';
import {
  BANK_CREDIT_ACCEPTED,
  BANK_CREDIT_REGISTERED,
  BANK_CREDIT_REJECTED,
  BANK_CREDIT_STATUSES,
  BANK_CREDIT_WAITING,
} from '../../constants/bankCreditStatuses';
import Button from '../../components/Button/Button';
import initialFeeCreditCardPayment from '../../api/deals/initialFeeCreditCardPayment';
import Alert from '../../components/Alert/Alert';
import extractErrorMessage from '../../utils/extractErrorMessage';
import {
  BANKS,
  DEAL_STEPS,
  DOCUMENT_TYPES_INVOICE,
  INSURANCE_SERVICE_ID_2_5,
  INSURANCE_SERVICE_ID_3,
  INSURANCE_SERVICE_ID_3_5,
} from '../../constants';
import generateCheck from '../../api/deals/generateCheck';
import selectCreditAppeal from '../../api/creditPlatform/selectCreditAppeal';
import getDealDocuments from '../../api/deals/getDealDocuments';
import CommissionPhotos from './DealCreate/FreedomFlow/CommissionPhotos';
import { stepAfterBankSelect } from './DealCreate/bankFirstStep';
import alert from '../../helpers/alert';
import LoaderInline from '../../components/Loader/LoaderInline';
import RoadAssistanceBadge from '../../components/CreditApplications/RoadAssistanceBadge';
import cancelCreditAppeal from '../../api/creditPlatform/forte/cancelCreditAppeal';
import CheckBankStatus from './DealCreate/CommonBankSteps/CheckBankStatus';
import DealConfigureSteps from './DealConfigureSteps';
import removeItemsToDeal from '../../api/deals/removeItemsToDeal';
import CancelCreditFunding from '../../components/CancelCreditFunding';
import ProcessDocumentButton from '../../components/ProcessDocumentButton/ProcessDocumentButton';
import processDocument from '../../api/bank/processDocument';
import UpdateCreditAppeal from './UpdateCreditAppeal';
import StepControlButtons from './DealCreate/CommonBankSteps/StepControlButtons';
import { bankErrorSummary, shouldShowBankStatus } from '../../utils/appealSubmitState';
import MyCarUpdateApplication, { needsMyCarUpdate, awaitingMyCarRework, isMyCarConditionCase } from './DealCreate/MyCarFlow/MyCarUpdateApplication';

const columns = CARS;

// Информационный статус банка лежит в json заявки: errorMessage занят решениями
// и его изменение рассылает клиенту push-уведомление.
const bankStatusName = (appeal) => {
  try {
    return JSON.parse(appeal?.json || '{}')?.bank_status_name || null;
  } catch (e) {
    return null;
  }
};

const getMock = (appeal) => ({
  ...appeal,
  statusId: BANK_CREDIT_ACCEPTED,
  bank: {
    id: BANKS.TEST,
    name: 'Test Bank',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/3/3f/Placeholder_view_vector.svg',
    body: '',
    api: 'http://localhost:8003',
  },
});

const PrintStyles = () => (
  <style>
    {`
      #printableArea {
        display: none;
      }
      @media print {
        body {
          visibility: hidden;
        }

        #sidebar {
          display: none;
        }
        #DealCreateForm {
          position: static;
        }

        #printableArea {
          display: block;
          visibility: visible;
          position: absolute;
          left: 0;
          top: 0;
        }
        #printableArea button {
          display: none;
        }
      }
    `}
  </style>
);

const DealCredits = ({ dealId, deal: data, onSubmit, documents, backHandler, services, orders, update }) => {
  const context = useParams();
  const id = context?.id || dealId;
  const [cars, setCars] = useState([]);
  // Пока машины не приехали, нельзя решить, нужен ли трёхсторонний договор:
  // по пустому списку стрелка «→» увела бы поручение мимо шага.
  const [carsLoaded, setCarsLoaded] = useState(false);
  let [equipmentsCount, updateCounters, deal] = useOutletContext() || [];
  deal = deal || data;
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [appeals, setAppeals] = useState([]);
  const [succes, setSucces] = useState({});
  const [subsidy, setSubsidy] = useState(null);
  const [chosenBankId, setChosenBankId] = useState(null);
  // Держим статус локально: init() перечитывает карточку сделки, поэтому после замены
  // машины (она снимает просрочку) плашка и кнопки обновляются без перезагрузки страницы.
  const [dealStatusId, setDealStatusId] = useState(deal?.statusId ?? deal?.status?.id ?? null);
  const isExpired = dealStatusId === DEAL_STATUS_TTL_EXPIRED;

  // Freedom для комиссионного и б/у авто оценивает залог по фото: без них /confirm
  // банк отклоняет. Фото грузятся до выбора предложения, бэкенд отправляет их в банк
  // при выборе Freedom (POST /api/bank/ffin/car-photo, Ffin.CollectCarPhotoSources).
  const [freedomPhotosReady, setFreedomPhotosReady] = useState(false);
  const isJetFin = (appeal) => (appeal?.bank?.id ?? appeal?.bankId) === BANKS.JETFIN;

  // Jet Finance присылает одобрение раньше условий: скоринг отвечает approved,
  // а суммы, срок, ставку и платёж банк даёт позже, в offer вебхука. Пока их нет,
  // в карточке подставлялись НАШИ запрошенные значения (ставка 10 % — вообще
  // умолчание при создании заявки), и менеджер видел их как условия банка.
  const conditionsNotReceived = (appeal) =>
    isJetFin(appeal) && !appeal?.other_conditions?.length && !appeal?.loan_amount;

  // Бывает, что банк прислал оффер, но ставка и платёж в нём нули (заявка 758891).
  // Печатать «0 %» нельзя: менеджер назовёт клиенту беспроцентный кредит.
  const rateUnknown = (appeal, condition) =>
    isJetFin(appeal) && !Number(condition?.rate ?? appeal?.percent);

  const paymentUnknown = (appeal, condition) =>
    isJetFin(appeal) && !Number(condition?.payment ?? appeal?.monthlyPayment);

  const freedomAppeal = appeals?.find((a) => (a?.bank?.id ?? a?.bankId) === BANKS.FREEDOM);
  const jetfinAppeal = appeals?.find((a) => (a?.bank?.id ?? a?.bankId) === BANKS.JETFIN);
  // Фото для оценки автомобиля нужны и Freedom, и Jet Finance. Загружаются в
  // одно место — документы сделки, а дальше каждый банк забирает их по-своему.
  const carPhotosAppeal = freedomAppeal || jetfinAppeal;
  const commissionCar = (() => {
    try {
      return !!JSON.parse(deal?.creditAppeal?.json || deal?.creditAppel?.json || '{}')?.initialRegistration;
    } catch (e) {
      return false;
    }
  })();
  const requiresCarPhotos = !!(cars?.[0]?.used || commissionCar);
  // Новая машина из салона: оценивать залог нечего, но Jet Finance ждёт снимок
  // VIN — без него заявка стоит в pre_approved (сделка 3248, заявка 759282).
  // Freedom по новой машине фото не просит, поэтому блок показываем только
  // при заявке в Jet Finance и выбор банка им не запираем.
  const newCarVinPhoto = !requiresCarPhotos && !!jetfinAppeal;

  useEffect(() => {
    try {
      const newSubsidy = JSON.parse(deal?.creditAppel?.json)?.subsidy;

      setSubsidy(newSubsidy);
    } catch (e) {}
  }, [deal]);

  const fetchAllAppeals = async (chunkId, page = 1, accumulated = []) => {
    const res = await getCreditAppeal({ chunkId, page });
    const allAppeals = [...accumulated, ...res.data?.appeals];
    if (res.data?.pagination?.hasNextPage) {
      return fetchAllAppeals(chunkId, page + 1, allAppeals);
    }
    return allAppeals;
  };

  const init = async () => {
    const { data } = await getDealCard({ id });
    setDealStatusId(data?.statusId ?? data?.status?.id ?? null);

    getDealCars({ id }).then((res) => {
      setCars(res.data?.cars || []);
      setCarsLoaded(true);
    });

    fetchAllAppeals(data?.chunkId)
      .then((appeals) => {
        const chosenBank = appeals?.find((appeal) => appeal?.selected);
        setChosenBankId(chosenBank?.bank?.id);
        setAppeals(appeals);
      })
      .finally(() => setLoadingPage(false));
  };

  React.useEffect(() => {
    init();
  }, []);

  const handeSubmit = async (cars) => {
    setLoading(true);
    try {
      cars = cars.map(({ value }) => ({ value }));

      const { data } = await getDealCard({ id });

      addItemsToDeal({
        dealId: String(id),
        clientId: data?.client?.id,
        cars,
      })
        .then(() => {
          setShowModal(false);
          updateCounters();
          init();
        })
        .catch((err) => alert(err))
        .finally(() => setLoading(false));
    } catch (e) {
      setLoading(false);
    }
  };

  const table = useTable(
    {
      columns,
      data: cars,
      disableSortRemove: true,
    },
    usePagination
  );

  const cardPayment = (appeal) => {
    setLoadingPage(true);
    initialFeeCreditCardPayment({
      bankId: appeal?.bank?.id,
      chunkId: deal?.chunkId,
    })
      .then(() => {
        setSucces({ message: 'Смс со ссылкой на оплату отправлено на номер телефона клиента' });
        onSubmit();
      })
      .catch((err) => {
        if (extractErrorMessage(err?.response?.data) == 'Заказ с указанным external_id уже существует') {
          return onSubmit();
        }
        setSucces({ message: extractErrorMessage(err?.response?.data) });
      })
      .finally(() => setLoadingPage(false));
  };
  const getInvoice = (appeal) => {
    getDealDocuments({ id: deal?.id })
      .then((res) => {
        setLoadingPage(true);
        const url = res?.data?.find((item) => item?.typeId === DOCUMENT_TYPES_INVOICE);
        if (url) {
          window.open(url?.url, '_blank');
          onSubmit();
        }
      })
      .finally(() => {
        setLoadingPage(false);
      });
  };

  const removeInsuranceFromDeal = async () => {
    const insuranceServices = services.filter((service) =>
      [INSURANCE_SERVICE_ID_2_5, INSURANCE_SERVICE_ID_3, INSURANCE_SERVICE_ID_3_5].includes(
        service.additionalServicesId
      )
    );

    if (insuranceServices?.length == 0) {
      return;
    }

    for (const service of insuranceServices) {
      await removeItemsToDeal({
        services: [{ value: service.id }],
        dealId: deal?.id,
      });
    }
  };

  const chooseBank = async (appeal, offerType) => {
    if ((appeal?.bank?.id ?? appeal?.bankId) === BANKS.FREEDOM && requiresCarPhotos && !freedomPhotosReady) {
      alert.error(
        'Сначала загрузите 5 фото авто (спереди, слева, справа, сзади, VIN) — блок «Фото авто для оценки залога» выше.'
      );
      return;
    }
    try {
      setLoadingPage(true);

      if (appeal?.bank?.id !== BANKS.FORTE) {
        await removeInsuranceFromDeal();
      }

      await selectCreditAppeal({ appealId: appeal?.id, offerType });
      if (appeal?.bank?.id !== BANKS.FORTE) {
        const forteAppeal = appeals.find((appeal) => appeal?.bank?.id === BANKS.FORTE);
        if (forteAppeal) {
          cancelCreditAppeal(forteAppeal?.id);
        }
      }
      await generateCheck({
        bankId: appeal.bankId,
        chunkId: data.chunkId,
      });

      onSubmit(appeal?.bankId, appeal);
    } catch (e) {
      setLoadingPage(false);
      alert.error(e?.response?.data?.message || 'Ошибка при выборе банка');
    }
  };

  const updateAppeals = () => {
    setLoadingPage(true);
    init();
  };

  const printForClient = () => {
    window.print();
  };

  // Тот же маршрут, что и у onBankSelect: своя копия карты «банк → шаг» уводила
  // поручение мимо трёхстороннего договора и уже разошлась с ней по EURAZ.
  const nextStep = useMemo(() => {
    const chosenAppeal = appeals.find((appeal) => appeal?.selected);
    if (!chosenAppeal?.bankId || !carsLoaded) {
      return null;
    }
    return stepAfterBankSelect(chosenAppeal.bankId, chosenAppeal, cars);
  }, [appeals, cars, carsLoaded]);

  return (
    <>
      <div className={dealId ? '' : styles.credit}>
        {carPhotosAppeal && (requiresCarPhotos || newCarVinPhoto) && (
          <CommissionPhotos
            appealId={carPhotosAppeal?.id}
            documents={documents}
            onReadyChange={setFreedomPhotosReady}
            banks={[freedomAppeal ? BANKS.FREEDOM : null, jetfinAppeal ? BANKS.JETFIN : null].filter(Boolean)}
            newCar={newCarVinPhoto}
          />
        )}
        <div className={styles.credit__main_info}>
          <div className="deal__card-title">
            <h3
              className={styles.title}
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Ответы от банков
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  marginLeft: 10,
                  cursor: 'pointer',
                }}
                onClick={updateAppeals}
              >
                <path
                  d="M5.463 4.43301C7.27756 2.86067 9.59899 1.99666 12 2.00001C17.523 2.00001 22 6.47701 22 12C22 14.136 21.33 16.116 20.19 17.74L17 12H20C20.0001 10.4316 19.5392 8.89781 18.6747 7.58927C17.8101 6.28072 16.5799 5.25517 15.1372 4.64013C13.6944 4.0251 12.1027 3.84771 10.56 4.13003C9.0172 4.41234 7.59145 5.14191 6.46 6.22801L5.463 4.43301ZM18.537 19.567C16.7224 21.1393 14.401 22.0034 12 22C6.477 22 2 17.523 2 12C2 9.86401 2.67 7.88401 3.81 6.26001L7 12H4C3.99987 13.5684 4.46075 15.1022 5.32534 16.4108C6.18992 17.7193 7.42007 18.7449 8.86282 19.3599C10.3056 19.9749 11.8973 20.1523 13.44 19.87C14.9828 19.5877 16.4085 18.8581 17.54 17.772L18.537 19.567Z"
                  fill="#B45436"
                />
              </svg>
              {nextStep ? (
                <div style={{ marginLeft: 'auto' }}>
                  <StepControlButtons dealId={id} nextStep={nextStep} update={update} setLoading={setLoadingPage} />
                </div>
              ) : null}
              <PrintStyles />
            </h3>
          </div>
          {/* {appeals[0]?.orders?.map((order) => {
                  return (
                    <p style={textStyles.fieldLabel}>
                      {order?.mark?.name} {order?.model?.name} {order?.complectation} / {order?.issueYear}
                    </p>
                  );
                })} */}
          {/* <p style={textStyles.fieldLabel}>Стоимость, тенге: {formatNumber(appeals[0]?.fullPrice)}</p>
                <p style={textStyles.fieldLabel}>
                  Запрашиваемый первоначальный взнос, тенге: {formatNumber(appeals[0]?.initialFee)}
                </p>
                <p style={textStyles.fieldLabel}>
                  Запрашиваемый срок кредитования: {formatNumber(appeals[0]?.period)} м.
                </p> */}
        </div>
        {isExpired && (
          <div style={{ background: '#FDEDED', border: '1px solid #EE7171', borderRadius: 8, padding: '10px 16px', margin: '0 20px 16px' }}>
            Банки не ответили в срок, автомобиль снят с брони. Подберите другой — сделка вернётся в работу.
          </div>
        )}
        {/* Машина в сделке одна на все офферы, поэтому кнопка общая — не внутри карточки банка. */}
        <div style={{ margin: '0 20px 16px' }}>
          <ReassignCarButton
            orderId={cars?.[0]?.orderId}
            complectationId={cars?.[0]?.complectationId}
            color={cars?.[0]?.color}
            onDone={init}
          />
        </div>
        {loadingPage ? (
          <LoaderInline />
        ) : (
          appeals.map((item) => {
            return (
              <div className={styles.bank}>
                <div className={styles.bank__row}>
                  <div className={styles.bank__img}>
                    <img src={item?.bank?.logo} />
                  </div>
                  <p className={styles.bank_name}>{item?.bank?.name}</p>
                  <p
                    className={`${styles.status} ${item?.statusId == BANK_CREDIT_ACCEPTED && styles.status__accepted} ${
                      item?.statusId == BANK_CREDIT_REJECTED && styles.status__rejected
                    }`}
                  >
                    {BANK_CREDIT_STATUSES[item?.statusId]}
                  </p>
                </div>
                <div style={{ padding: '0 20px' }}>
                  {item?.bank?.id == BANKS.HALYK ? (
                    <p className={styles.sub} style={{ marginBottom: 5 }}>
                      Субсидия: {subsidy ? 'Да' : 'Нет'}
                    </p>
                  ) : null}
                  <p className={styles.sub} style={{ marginBottom: 5 }}>
                    bankUniqueId: {item?.bankUniqueId}
                  </p>
                  <p className={styles.sub} style={{ marginBottom: 5 }}>
                    bankOwnerId: {item?.bankOwnerId}
                  </p>
                </div>
                {item?.errorMessage ? (
                  <div style={{ padding: '0 20px', marginBottom: 20 }}>
                    <p className={styles.sub} style={{ color: '#EE7171', wordBreak: 'break-all' }}>
                      {bankErrorSummary(item?.errorMessage)}
                    </p>
                  </div>
                ) : null}
                {/* «Одобрено с доработкой»: блок нужен и на шаге «Банк» внутри сделки,
                    а не только на вкладке «Банки» — менеджер видит карточку здесь. */}
                {isMyCarConditionCase(item, BANKS.MYCAR) ? (
                  <div style={{ padding: '0 20px', marginBottom: 20 }}>
                    <p className={styles.sub}>
                      Банк одобрил заявку на своих условиях — они показаны ниже. Если клиент
                      согласен, нажмите «Выбрать предложение»: согласие уйдёт в MyCar.
                    </p>
                  </div>
                ) : null}
                {needsMyCarUpdate(item, BANKS.MYCAR) ? (
                  <div style={{ padding: '0 20px' }}>
                    <MyCarUpdateApplication
                      appealId={item?.id}
                      onSuccess={() => {
                        init();
                        update && update();
                      }}
                    />
                  </div>
                ) : null}
                {/* Информационный статус банка («Ожидает рассмотрения» у Shinhan) —
                    не отказ, поэтому серым, а не красным. Бэкенд кладёт его в json
                    заявки: errorMessage занят решениями и шлёт клиенту push. */}
                {shouldShowBankStatus(item) && bankStatusName(item) ? (
                  <div style={{ padding: '0 20px', marginBottom: 20 }}>
                    <p className={styles.sub}>{bankStatusName(item)}</p>
                  </div>
                ) : null}
                <div
                  style={{
                    padding: '0 20px',
                    gap: 20,
                    marginTop: 20,
                    marginBottom: 20,
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                  }}
                >
                  {item?.bankId != BANKS.SHINHAN ? (
                    <CheckBankStatus
                      bankId={item?.bankId}
                      onSuccess={updateAppeals}
                      chunkId={deal?.chunkId}
                      setLoading={setLoadingPage}
                      // Forte решение сам не присылает — его нужно запросить.
                      // По остальным банкам ответ приходит без нашего участия,
                      // и кнопка там просто обновляет карточку.
                      text={item?.bankId == BANKS.FORTE ? 'Получить ответ Forte' : 'Обновить'}
                      hint={
                        item?.bankId == BANKS.FORTE
                          ? 'Forte не присылает решение сам. Нажмите, чтобы запросить ответ банка — одобрение или отказ придёт в карточку.'
                          : undefined
                      }
                    />
                  ) : null}
                  {item.bankId == BANKS.EURAZ && item?.statusId == BANK_CREDIT_WAITING ? (
                    <div>
                      <ProcessDocumentButton chunkId={deal?.chunkId} />
                    </div>
                  ) : null}
                  {item?.bankId == BANKS.BCK && item?.statusId == BANK_CREDIT_WAITING && (
                    <UpdateCreditAppeal
                      appeal={item}
                      orders={orders}
                      deal={deal}
                      documents={documents}
                      update={update}
                    />
                  )}
                </div>
                {(item?.statusId == BANK_CREDIT_ACCEPTED || item?.statusId == BANK_CREDIT_REGISTERED) && (
                  <>
                    <div>
                      <p className={styles.sub}>
                        {conditionsNotReceived(item)
                          ? 'Банк предварительно одобрил заявку. Условия ещё не пришли.'
                          : 'Клиенту одобрен кредит на следующих условиях:'}
                      </p>
                      {conditionsNotReceived(item) ? (
                        <div
                          style={{
                            background: '#FFF6E9',
                            border: '1px solid #E8C89A',
                            borderRadius: 4,
                            padding: '12px 16px',
                            marginBottom: 20,
                            color: '#05141F',
                            fontSize: 14,
                          }}
                        >
                          Скоринг пройден, но банк ещё не прислал сумму, срок, ставку и платёж.
                          Нажмите «Обновить» через несколько минут — как только условия придут,
                          они появятся здесь.
                          <br />
                          <br />
                          Решение предварительное. Банк может отказать и после него — по проверке
                          службы безопасности, решению кредитного комитета или по истечении срока
                          заявки. Окончательным считается «Кредит выдан».
                        </div>
                      ) : item?.other_conditions?.length ? (
                        item?.other_conditions?.map((condition) => {
                          return (
                            <>
                              <div style={{ background: '#F7F7F7', padding: '10px 20px', marginBottom: 20 }}>
                                <RoadAssistanceBadge condition={condition} />
                                <div className={styles.bank__info} style={{ marginBottom: 20 }}>
                                  <div
                                    className={styles.bank__info_row}
                                    style={{
                                      gridColumn: '1 / span 2',
                                    }}
                                  >
                                    <p className={styles.bank__info_title}>Сумма займа</p>
                                    <p className={styles.bank__info_sub}>{formatNumber(condition?.amount)}</p>
                                  </div>
                                  <div className={styles.bank__info_row}>
                                    <p className={styles.bank__info_title}>Первоначальный взнос, тенге</p>
                                    <p className={styles.bank__info_sub}>{formatNumber(condition?.initialFee)}</p>
                                  </div>
                                  {item.bank.id !== BANKS.ALTYN &&
                                  item.bank.id !== BANKS.SHINHAN &&
                                  condition?.insurance !== false &&
                                  ((condition?.kasko_amount != null && condition?.kasko_amount !== 0) ||
                                    (item?.kasko_amount != null && item?.kasko_amount !== 0) ||
                                    (condition?.cascoAmount != null && condition?.cascoAmount !== 0) ||
                                    (item?.cascoAmount != null && item?.cascoAmount !== 0)) ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Страхование КАСКО</p>
                                      <p className={styles.bank__info_sub}>
                                        {formatNumber(
                                          condition?.cascoAmount != null && condition.cascoAmount !== 0
                                            ? condition.cascoAmount
                                            : item?.cascoAmount != null && item.cascoAmount !== 0
                                            ? item.cascoAmount
                                            : condition?.kasko_amount != null && condition?.kasko_amount !== 0
                                            ? condition.kasko_amount
                                            : item?.kasko_amount != null && item?.kasko_amount !== 0
                                            ? item.kasko_amount
                                            : 0
                                        )}
                                      </p>
                                    </div>
                                  ) : null}
                                  {item.bank.id !== BANKS.HALYK && (condition?.ns_amount || item?.ns_amount) ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Страхование НС</p>
                                      <p className={styles.bank__info_sub}>
                                        {formatNumber(
                                          condition?.ns_amount != null && condition.ns_amount !== 0
                                            ? condition.ns_amount
                                            : item?.ns_amount != null && item.ns_amount !== 0
                                            ? item.ns_amount
                                            : 0
                                        )}
                                      </p>
                                    </div>
                                  ) : null}
                                  {condition?.cascoTerm > 0 ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Срок страхования КАСКО</p>
                                      <p className={styles.bank__info_sub}>{condition?.cascoTerm} м.</p>
                                    </div>
                                  ) : null}
                                  {condition?.road_assistance !== false && condition?.road_assistance_term > 0 ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Срок страхования</p>
                                      <p className={styles.bank__info_sub}>{condition?.road_assistance_term}</p>
                                    </div>
                                  ) : null}
                                  {condition?.road_assistance !== false &&
                                  condition?.road_assistance_name &&
                                  condition?.road_assistance_sum ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>
                                        Страхование {condition?.road_assistance_name}
                                      </p>
                                      <p className={styles.bank__info_sub}>{condition?.road_assistance_sum}</p>
                                    </div>
                                  ) : null}
                                  {condition?.comission ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Комиссия банка, тенге:</p>
                                      <p className={styles.bank__info_sub}>
                                        {formatNumber(condition?.comissionSum)}
                                      </p>
                                    </div>
                                  ) : null}

                                  <div className={styles.bank__info_row}>
                                    <p className={styles.bank__info_title}>Срок кредитования:</p>
                                    <p className={styles.bank__info_sub}>{condition?.term} м.</p>
                                  </div>
                                  <div className={styles.bank__info_row}>
                                    <p className={styles.bank__info_title}>Процентная ставка (годовых):</p>
                                    <p className={styles.bank__info_sub}>
                                      {rateUnknown(item, condition) ? 'банк не прислал' : `${condition?.rate}%`}
                                    </p>
                                  </div>
                                  {item.bank.id != BANKS.EURAZ && (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Ежемесячный платеж, тенге:</p>
                                      <p className={styles.bank__info_sub}>
                                        {paymentUnknown(item, condition)
                                          ? 'банк не прислал'
                                          : formatNumber(condition?.payment)}
                                      </p>
                                    </div>
                                  )}
                                  {condition?.secondRateMonthlyPayment ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>
                                      Ежемесячный платеж во втором периоде по окончанию льготного периода:
                                      </p>
                                      <p className={styles.bank__info_sub}>{condition?.secondRateMonthlyPayment}</p>
                                    </div>
                                  ) : null}
                                  {condition?.secondPayment ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Ежемесячный платеж:</p>
                                      <p className={styles.bank__info_sub}>{condition?.secondPayment}</p>
                                    </div>
                                  ) : null}
                                  {item?.bank?.id == BANKS.EURAZ && condition?.additionalServices ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Доп. услуги:</p>
                                      <p className={styles.bank__info_sub}>{condition?.additionalServices}</p>
                                    </div>
                                  ) : null}
                                  {item?.bank?.id == BANKS.SAPA &&
                                  (condition?.life_insurance_amount || item?.life_insurance_amount) ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Страхование жизни:</p>
                                      <p className={styles.bank__info_sub}>
                                        {condition?.life_insurance_amount || item?.life_insurance_amount}
                                      </p>
                                    </div>
                                  ) : null}
                                  {item?.bank?.id == BANKS.SAPA &&
                                  (condition?.credit_holiday_amount || item?.credit_holiday_amount) ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Помощь на дороге:</p>
                                      <p className={styles.bank__info_sub}>
                                        {formatNumber(condition?.credit_holiday_amount || item?.credit_holiday_amount)}
                                      </p>
                                    </div>
                                  ) : null}
                                  {item?.bank?.id == BANKS.SAPA &&
                                  (condition?.car_check_amount || item?.car_check_amount) ? (
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Проверка авто:</p>
                                      <p className={styles.bank__info_sub}>
                                        {formatNumber(condition?.car_check_amount || item?.car_check_amount)}
                                      </p>
                                    </div>
                                  ) : null}
                                </div>

                                {condition?.conditions?.length ? (
                                  <>
                                    <div
                                      style={{
                                        display: 'grid',
                                        gap: 10,
                                        gridTemplateColumns: '20px 1fr',
                                        marginBottom: 20,
                                      }}
                                    >
                                      <RiErrorWarningLine height={20} width={20} color="#B45436" />
                                      <p
                                        style={{
                                          color: '#05141F',
                                          fontSize: 14,
                                          fontWeight: 600,
                                        }}
                                      >
                                        Вы можете выбрать этот вариант в случае погашения кредитов по следующим
                                        договорам:
                                      </p>
                                    </div>
                                    {condition?.conditions?.map((openedOffer) => {
                                      return (
                                        <>
                                          <div
                                            className={styles.bank__info}
                                            style={{ background: '#fff', padding: 10, marginBottom: 20 }}
                                          >
                                            <div className={styles.bank__info_row}>
                                              <p className={styles.bank__info_title}>Номер договора</p>
                                              <p className={styles.bank__info_sub} style={{ wordBreak: 'break-all' }}>
                                                {openedOffer?.contractNumber}
                                              </p>
                                            </div>
                                            {item.bank.id != BANKS.EURAZ && (
                                              <div className={styles.bank__info_row}>
                                                <p className={styles.bank__info_title}>Ежемесячный платеж, тенге:</p>
                                                <p className={styles.bank__info_sub}>
                                                  {formatNumber(openedOffer?.monthlyInstalmentAmountKZ)}
                                                </p>
                                              </div>
                                            )}

                                            {condition?.secondRateMonthlyPayment ? (
                                              <div className={styles.bank__info_row}>
                                                <p className={styles.bank__info_title}>
                                                Ежемесячный платеж во втором периоде по окончанию льготного периода:
                                                </p>
                                                <p className={styles.bank__info_sub}>
                                                  {condition?.secondRateMonthlyPayment}
                                                </p>
                                              </div>
                                            ) : null}
                                            {condition?.secondPayment ? (
                                              <div className={styles.bank__info_row}>
                                                <p className={styles.bank__info_title}>Ежемесячный платеж:</p>
                                                <p className={styles.bank__info_sub}>
                                                  {condition?.secondPayment}
                                                </p>
                                              </div>
                                            ) : null}
                                            <div className={styles.bank__info_row}>
                                              <p className={styles.bank__info_title}>Банк:</p>
                                              <p className={styles.bank__info_sub}>{openedOffer?.finInstiut}</p>
                                            </div>
                                            <div className={styles.bank__info_row}>
                                              <p className={styles.bank__info_title}>Остаток погашению, тенге:</p>
                                              <p className={styles.bank__info_sub}>
                                                {formatNumber(openedOffer?.outstandingAmountKZ)}
                                              </p>
                                            </div>
                                            {item?.bank?.id == BANKS.SAPA &&
                                            (condition?.life_insurance_amount || item?.life_insurance_amount) ? (
                                              <div className={styles.bank__info_row}>
                                                <p className={styles.bank__info_title}>Страхование жизни:</p>
                                                <p className={styles.bank__info_sub}>
                                                  {condition?.life_insurance_amount || item?.life_insurance_amount}
                                                </p>
                                              </div>
                                            ) : null}
                                          </div>
                                        </>
                                      );
                                    })}
                                  </>
                                ) : null}

                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    marginTop: 20,
                                  }}
                                >
                                  {/* {item?.selected && deal?.paymentType?.id == PAYMENT_TYPES_CREDIT_CARD && (
                                        <Button
                                          text="Оплатить картой"
                                          fill
                                          onClick={() => cardPayment(item, condition)}
                                        />
                                      )}
                                      {item?.selected && deal?.paymentType?.id == PAYMENT_TYPES_CREDIT_INVOICE && (
                                        <Button text="Перейти в документы" fill onClick={() => getInvoice(item)} />
                                      )} */}
                                  {
                                    <Button
                                      text="Выбрать предложение"
                                      fill
                                      disabled={isExpired || awaitingMyCarRework(item, BANKS.MYCAR)}
                                      onClick={() => chooseBank(item, condition?.offerType)}
                                    />
                                  }
                                </div>
                              </div>
                            </>
                          );
                        })
                      ) : (
                        <>
                          <div className={styles.bank__info}>
                            {item?.loan_amount ? (
                              <div className={styles.bank__info_row} style={{ gridColumn: '1 / span 2' }}>
                                <p className={styles.bank__info_title}>Сумма займа</p>
                                <p className={styles.bank__info_sub}>{formatNumber(item?.loan_amount)}</p>
                              </div>
                            ) : null}

                            <div className={styles.bank__info_row}>
                              <p className={styles.bank__info_title}>Первоначальный взнос, тенге</p>
                              <p className={styles.bank__info_sub}>{formatNumber(item?.initialFee)}</p>
                            </div>
                            <div className={styles.bank__info_row}>
                              <p className={styles.bank__info_title}>Срок кредитования:</p>
                              <p className={styles.bank__info_sub}>{item?.period} м.</p>
                            </div>
                            {item?.road_assistance_term > 0 ? (
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Срок страхования</p>
                                <p className={styles.bank__info_sub}>{item?.road_assistance_term}</p>
                              </div>
                            ) : null}
                            {item?.road_assistance_name && item?.road_assistance_sum ? (
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Страхование {item?.road_assistance_name}</p>
                                <p className={styles.bank__info_sub}>{item?.road_assistance_sum}</p>
                              </div>
                            ) : null}
                            {item.bank.id !== BANKS.SHINHAN &&
                            (item?.cascoAmount != null || item?.kasko_amount != null) ? (
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Страхование КАСКО</p>
                                <p className={styles.bank__info_sub}>
                                  {formatNumber(
                                    item?.cascoAmount != null && item.cascoAmount !== 0
                                      ? item.cascoAmount
                                      : item?.kasko_amount === 0 || item?.kasko_amount === null
                                      ? 0
                                      : item?.kasko_amount
                                  )}
                                </p>
                              </div>
                            ) : null}
                            {item?.ns_amount ? (
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Страхование НС</p>
                                <p className={styles.bank__info_sub}>{formatNumber(item?.ns_amount)}</p>
                              </div>
                            ) : null}
                            {item?.cascoTerm > 0 ? (
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Срок страхования КАСКО</p>
                                <p className={styles.bank__info_sub}>{item?.cascoTerm} м.</p>
                              </div>
                            ) : null}
                            <div className={styles.bank__info_row}>
                              <p className={styles.bank__info_title}>Процентная ставка (годовых):</p>
                              <p className={styles.bank__info_sub}>
                                {rateUnknown(item, null) ? 'банк не прислал' : `${item?.percent}%`}
                              </p>
                            </div>
                            {item.bank.id != BANKS.EURAZ && (
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Ежемесячный платеж, тенге:</p>
                                <p className={styles.bank__info_sub}>
                                  {paymentUnknown(item, null)
                                    ? 'банк не прислал'
                                    : formatNumber(item?.monthlyPayment)}
                                </p>
                              </div>
                            )}

                            {item?.secondRateMonthlyPayment ? (
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>
                                Ежемесячный платеж во втором периоде по окончанию льготного периода:
                                </p>
                                <p className={styles.bank__info_sub}>{item?.secondRateMonthlyPayment}</p>
                              </div>
                            ) : null}
                            {item?.secondPayment ? (
                              <div className={styles.bank__info_row}>
                                  <p className={styles.bank__info_title}>Второй платеж по кредиту:</p>
                                  <p className={styles.bank__info_sub}>{item?.secondPayment}</p>
                              </div>
                            ) : null}
                            {item?.bank?.id == BANKS.HALYK ? (
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Субсидия:</p>
                                <p className={styles.bank__info_sub}>{subsidy ? 'Да' : 'Нет'}</p>
                              </div>
                            ) : null}
                            {item?.bank?.id == BANKS.EURAZ && item?.additionalServices ? (
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Доп. услуги:</p>
                                <p className={styles.bank__info_sub}>{item?.additionalServices}</p>
                              </div>
                            ) : null}
                          </div>
                          {isJetFin(item) ? (
                            <p style={{ color: '#7A6A55', fontSize: 13, marginTop: 10 }}>
                              Решение предварительное: банк может отказать и после одобрения
                              скоринга — по проверке службы безопасности или решению кредитного
                              комитета. Окончательным считается «Кредит выдан».
                            </p>
                          ) : null}
                          <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 20 }}>
                            {/* {item?.selected && deal?.paymentType?.id == PAYMENT_TYPES_CREDIT_CARD && (
                                  <Button text="Оплатить картой" fill onClick={() => cardPayment(item)} />
                                )}
                                {item?.selected && deal?.paymentType?.id == PAYMENT_TYPES_CREDIT_INVOICE && (
                                  <Button text="Перейти в документы" fill onClick={() => getInvoice(item)} />
                                )} */}
                            {
                              <Button
                                text="Выбрать предложение"
                                fill
                                disabled={isExpired || awaitingMyCarRework(item, BANKS.MYCAR)}
                                onClick={() => chooseBank(item)}
                              />
                            }
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}

        <div id="printableArea">
          <h3
            className={styles.title}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 20,
            }}
          >
            Ответы от банков
          </h3>
          {appeals
            ?.filter((appeal) => appeal?.statusId == BANK_CREDIT_ACCEPTED || appeal?.statusId == BANK_CREDIT_REGISTERED)
            .map((item) => {
              return (
                <div className={styles.bank}>
                  <div className={styles.bank__row}>
                    <div className={styles.bank__img}>
                      <img src={item?.bank?.logo} />
                    </div>
                    <p className={styles.bank_name}>{item?.bank?.name}</p>
                    <p
                      className={`${styles.status} ${
                        item?.statusId == BANK_CREDIT_ACCEPTED && styles.status__accepted
                      } ${item?.statusId == BANK_CREDIT_REJECTED && styles.status__rejected}`}
                    >
                      {BANK_CREDIT_STATUSES[item?.statusId]}
                    </p>
                  </div>
                  {item?.errorMessage && (
                    <div style={{ padding: '0 20px', marginBottom: 20 }}>
                      <p className={styles.sub} style={{ color: '#EE7171', wordBreak: 'break-all' }}>
                        {bankErrorSummary(item?.errorMessage)}
                      </p>
                    </div>
                  )}
                  {/* «Одобрено с доработкой»: банк ждёт документ — отправляем из карточки. */}
                  {isMyCarConditionCase(item, BANKS.MYCAR) && (
                    <div style={{ padding: '0 20px', marginBottom: 20 }}>
                      <p className={styles.sub}>
                        Банк одобрил заявку на своих условиях — они показаны ниже. Если клиент
                        согласен, нажмите «Выбрать предложение»: согласие уйдёт в MyCar.
                      </p>
                    </div>
                  )}
                  {needsMyCarUpdate(item, BANKS.MYCAR) && (
                    <div style={{ padding: '0 20px' }}>
                      <MyCarUpdateApplication
                      appealId={item?.id}
                      onSuccess={() => {
                        init();
                        update && update();
                      }}
                    />
                    </div>
                  )}
                  {(item?.statusId == BANK_CREDIT_ACCEPTED || item?.statusId == BANK_CREDIT_REGISTERED) && (
                    <>
                      <div>
                        <p className={styles.sub}>
                        {conditionsNotReceived(item)
                          ? 'Банк предварительно одобрил заявку. Условия ещё не пришли.'
                          : 'Клиенту одобрен кредит на следующих условиях:'}
                      </p>
                        {conditionsNotReceived(item) ? (
                        <div
                          style={{
                            background: '#FFF6E9',
                            border: '1px solid #E8C89A',
                            borderRadius: 4,
                            padding: '12px 16px',
                            marginBottom: 20,
                            color: '#05141F',
                            fontSize: 14,
                          }}
                        >
                          Скоринг пройден, но банк ещё не прислал сумму, срок, ставку и платёж.
                          Нажмите «Обновить» через несколько минут — как только условия придут,
                          они появятся здесь.
                          <br />
                          <br />
                          Решение предварительное. Банк может отказать и после него — по проверке
                          службы безопасности, решению кредитного комитета или по истечении срока
                          заявки. Окончательным считается «Кредит выдан».
                        </div>
                      ) : item?.other_conditions?.length ? (
                          item?.other_conditions?.map((condition) => {
                            return (
                              <>
                                <div style={{ background: '#F7F7F7', padding: '10px 20px', marginBottom: 20 }}>
                                  <RoadAssistanceBadge condition={condition} />
                                  <div className={styles.bank__info} style={{ marginBottom: 20 }}>
                                    <div
                                      className={styles.bank__info_row}
                                      style={{
                                        gridColumn: '1 / span 2',
                                      }}
                                    >
                                      <p className={styles.bank__info_title}>Сумма займа</p>
                                      <p className={styles.bank__info_sub}>{formatNumber(condition?.amount)}</p>
                                    </div>
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Первоначальный взнос, тенге</p>
                                      <p className={styles.bank__info_sub}>
                                        {condition?.initialFee
                                          ? formatNumber(condition?.initialFee)
                                          : formatNumber(item?.initialFee)}
                                      </p>
                                    </div>
                                    {item.bank.id != BANKS.EURAZ && (
                                      <div className={styles.bank__info_row}>
                                        <p className={styles.bank__info_title}>Ежемесячный платеж, тенге:</p>
                                        <p className={styles.bank__info_sub}>
                                          {formatNumber(condition?.monthlyInstalmentAmountKZ)}
                                        </p>
                                      </div>
                                    )}

                                    {condition?.secondRateMonthlyPayment ? (
                                      <div className={styles.bank__info_row}>
                                        <p className={styles.bank__info_title}>
                                        Ежемесячный платеж во втором периоде по окончанию льготного периода:
                                        </p>
                                        <p className={styles.bank__info_sub}>{condition?.secondRateMonthlyPayment}</p>
                                      </div>
                                    ) : null}
                                    {condition?.secondPayment ? (
                                      <div className={styles.bank__info_row}>
                                        <p className={styles.bank__info_title}>Ежемесячный платеж:</p>
                                        <p className={styles.bank__info_sub}>{condition?.secondPayment}</p>
                                      </div>
                                    ) : null}
                                    {condition?.comission ? (
                                      <div className={styles.bank__info_row}>
                                        <p className={styles.bank__info_title}>Комиссия банка, тенге:</p>
                                        <p className={styles.bank__info_sub}>{formatNumber(condition?.comissionSum)}</p>
                                      </div>
                                    ) : null}
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Срок кредитования:</p>
                                      <p className={styles.bank__info_sub}>{condition?.term} м.</p>
                                    </div>
                                    <div className={styles.bank__info_row}>
                                      <p className={styles.bank__info_title}>Процентная ставка (годовых):</p>
                                      <p className={styles.bank__info_sub}>{condition?.rate}%</p>
                                    </div>
                                    {item.bank.id != BANKS.EURAZ && (
                                      <div className={styles.bank__info_row}>
                                        <p className={styles.bank__info_title}>Ежемесячный платеж, тенге:</p>
                                        <p className={styles.bank__info_sub}>{formatNumber(condition?.payment)}</p>
                                      </div>
                                    )}
                                  </div>

                                  {condition?.conditions?.length ? (
                                    <>
                                      <div
                                        style={{
                                          display: 'grid',
                                          gap: 10,
                                          gridTemplateColumns: '20px 1fr',
                                          marginBottom: 20,
                                        }}
                                      >
                                        <RiErrorWarningLine height={20} width={20} color="#B45436" />
                                        <p
                                          style={{
                                            color: '#05141F',
                                            fontSize: 14,
                                            fontWeight: 600,
                                          }}
                                        >
                                          Вы можете выбрать этот вариант в случае погашения кредитов по следующим
                                          договорам:
                                        </p>
                                      </div>
                                      {condition?.conditions?.map((openedOffer) => {
                                        return (
                                          <>
                                            <div
                                              className={styles.bank__info}
                                              style={{ background: '#fff', padding: 10, marginBottom: 20 }}
                                            >
                                              <div className={styles.bank__info_row}>
                                                <p className={styles.bank__info_title}>Номер договора</p>
                                                <p className={styles.bank__info_sub} style={{ wordBreak: 'break-all' }}>
                                                  {openedOffer?.contractNumber}
                                                </p>
                                              </div>
                                              {item.bank.id != BANKS.EURAZ && (
                                                <div className={styles.bank__info_row}>
                                                  <p className={styles.bank__info_title}>Ежемесячный платеж, тенге:</p>
                                                  <p className={styles.bank__info_sub}>
                                                    {formatNumber(openedOffer?.monthlyInstalmentAmountKZ)}
                                                  </p>
                                                </div>
                                              )}

                                              {condition?.secondRateMonthlyPayment || item?.secondRateMonthlyPayment ? (
                                                <div className={styles.bank__info_row}>
                                                  <p className={styles.bank__info_title}>
                                                  Ежемесячный платеж во втором периоде по окончанию льготного периода:
                                                  </p>
                                                  <p className={styles.bank__info_sub}>
                                                    {condition?.secondRateMonthlyPayment ||
                                                      item?.secondRateMonthlyPayment}
                                                  </p>
                                                </div>
                                              ) : null}
                                              {condition?.secondPayment || item?.secondPayment ? (
                                                <div className={styles.bank__info_row}>
                                                  <p className={styles.bank__info_title}>Ежемесячный платеж:</p>
                                                  <p className={styles.bank__info_sub}>
                                                    {condition?.secondPayment || item?.secondPayment}
                                                  </p>
                                                </div>
                                              ) : null}
                                              <div className={styles.bank__info_row}>
                                                <p className={styles.bank__info_title}>Банк:</p>
                                                <p className={styles.bank__info_sub}>{openedOffer?.finInstiut}</p>
                                              </div>
                                              <div className={styles.bank__info_row}>
                                                <p className={styles.bank__info_title}>Остаток погашению, тенге:</p>
                                                <p className={styles.bank__info_sub}>
                                                  {formatNumber(openedOffer?.outstandingAmountKZ)}
                                                </p>
                                              </div>
                                              {item?.bank?.id == BANKS.SAPA &&
                                              (condition?.life_insurance_amount || item?.life_insurance_amount) ? (
                                                <div className={styles.bank__info_row}>
                                                  <p className={styles.bank__info_title}>Страхование жизни:</p>
                                                  <p className={styles.bank__info_sub}>
                                                    {condition?.life_insurance_amount || item?.life_insurance_amount}
                                                  </p>
                                                </div>
                                              ) : null}
                                            </div>
                                          </>
                                        );
                                      })}
                                    </>
                                  ) : null}

                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'flex-start',
                                      marginTop: 20,
                                    }}
                                  >
                                    {/* {item?.selected && deal?.paymentType?.id == PAYMENT_TYPES_CREDIT_CARD && (
                                        <Button
                                          text="Оплатить картой"
                                          fill
                                          onClick={() => cardPayment(item, condition)}
                                        />
                                      )}
                                      {item?.selected && deal?.paymentType?.id == PAYMENT_TYPES_CREDIT_INVOICE && (
                                        <Button text="Перейти в документы" fill onClick={() => getInvoice(item)} />
                                      )} */}
                                    {
                                      <Button
                                        text="Выбрать предложение"
                                        fill
                                        disabled={isExpired}
                                        onClick={() => chooseBank(item, condition?.offerType)}
                                      />
                                    }
                                  </div>
                                </div>
                              </>
                            );
                          })
                        ) : (
                          <>
                            <div className={styles.bank__info}>
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Сумма займа</p>
                                <p className={styles.bank__info_sub}>{formatNumber(item?.fullPrice)}</p>
                              </div>
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Первоначальный взнос, тенге</p>
                                <p className={styles.bank__info_sub}>{formatNumber(item?.initialFee)}</p>
                              </div>
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Срок кредитования:</p>
                                <p className={styles.bank__info_sub}>{item?.period} м.</p>
                              </div>
                              <div className={styles.bank__info_row}>
                                <p className={styles.bank__info_title}>Процентная ставка (годовых):</p>
                                <p className={styles.bank__info_sub}>{item?.percent}%</p>
                              </div>
                              {item.bank.id != BANKS.EURAZ && (
                                <div className={styles.bank__info_row}>
                                  <p className={styles.bank__info_title}>Ежемесячный платеж, тенге:</p>
                                  <p className={styles.bank__info_sub}>{formatNumber(item?.monthlyPayment)}</p>
                                </div>
                              )}
                            </div>
                            {isJetFin(item) ? (
                            <p style={{ color: '#7A6A55', fontSize: 13, marginTop: 10 }}>
                              Решение предварительное: банк может отказать и после одобрения
                              скоринга — по проверке службы безопасности или решению кредитного
                              комитета. Окончательным считается «Кредит выдан».
                            </p>
                          ) : null}
                          <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 20 }}>
                              {/* {item?.selected && deal?.paymentType?.id == PAYMENT_TYPES_CREDIT_CARD && (
                                  <Button text="Оплатить картой" fill onClick={() => cardPayment(item)} />
                                )}
                                {item?.selected && deal?.paymentType?.id == PAYMENT_TYPES_CREDIT_INVOICE && (
                                  <Button text="Перейти в документы" fill onClick={() => getInvoice(item)} />
                                )} */}
                              {
                              <Button
                                text="Выбрать предложение"
                                fill
                                disabled={isExpired || awaitingMyCarRework(item, BANKS.MYCAR)}
                                onClick={() => chooseBank(item)}
                              />
                            }
                            </div>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
        </div>
        <div style={{ marginBottom: 20, padding: '0 20px' }}>
          <Button onClick={printForClient} fill>
            Распечатать для клиента
          </Button>
        </div>
      </div>

      {deal?.step === DEAL_STEPS.FINISH && (
        <CancelCreditFunding
          dealId={deal?.id}
          bankOwnerId={deal?.creditAppeal?.bankOwnerId}
          chunkId={deal?.chunkId}
          buttonText="Отменить заявку и финансирование Eurasian bank"
        />
      )}

      {/* update, а не onSubmit: onSubmit здесь — onBankSelect, и он тут же
          перекидывал сделку на первый шаг банка, затирая только что выбранный
          админом этап (в том числе новый «Трёхсторонний договор»). */}
      <DealConfigureSteps bankId={chosenBankId} dealId={deal?.id} update={update} />
      {succes.message && <Alert message={succes.message} onClick={setSucces} />}
    </>
  );
};

export default DealCredits;
